/**
 * Expense-coverage runtime helpers (INS-12686).
 *
 * Split from `types.ts` deliberately: every other `types.ts` under `eval3-new/`
 * is type-only, and a runtime export living in one of them is invisible to a
 * reader scanning for "what actually executes here". Types stay in `./types`,
 * the functions that read them live here.
 */

import { countText } from 'utils/number';
import { joinClauses, pluralize } from 'utils/strings';
import type {
  CaseDomain,
  DomainScenarioOption,
  RunScenarioRow,
} from '../types';
import type {
  CoverageCostType,
  ExpenseScenarioOption,
  UncoveredArea,
} from './types';

/**
 * Narrows the `useDomainScenarios('expenses')` list — typed as the shared
 * `DomainScenarioOption[]` even though the expenses wire really carries the
 * coverage fields — to the rows that actually carry them.
 *
 * Runtime-safe by design, NOT a bare cast, and it defaults nothing: a server
 * that has not been deployed yet (or an old cached response from one that
 * hadn't) simply yields `[]`. Defaulting a missing count to 0 would be
 * indistinguishable from a real "fully covered" answer, and casting would render
 * the literal "NaN of undefined areas" at the user.
 *
 * Dropping the row is only half the answer, though: a dropped row leaves its
 * scenario unresolvable, which is exactly the "could not be checked" state the
 * callout now SAYS out loud rather than rendering as blank space. Silence about
 * a scenario nothing could read is what made a real gap look like a clean check.
 *
 * @param {DomainScenarioOption[] | undefined} options - The domain list, possibly still loading.
 * @return {ExpenseScenarioOption[]} Only the rows carrying the coverage fields, in list order.
 */
export function asExpenseScenarioOptions(
  options: DomainScenarioOption[] | undefined
): ExpenseScenarioOption[] {
  return (options ?? []).filter((option): option is ExpenseScenarioOption => {
    const candidate = option as Partial<ExpenseScenarioOption>;
    return (
      typeof candidate.areaCount === 'number' &&
      Array.isArray(candidate.uncoveredAreas)
    );
  });
}

/**
 * The run's Expenses row, for the callout. While `run_scenarios` answers a changed
 * override, the rows on hand are the previous overrides' (`isPreviousData`). They
 * still describe the run's Expenses when only another domain or the deck changed,
 * so the callout keeps them rather than blinking out; when the expenses pick
 * itself changed they may name the replaced scenario, so it gets none.
 *
 * @param {RunScenarioRow[] | undefined} rows - The `run_scenarios` rows on hand.
 * @param {boolean} isPreviousData - Whether they answer earlier overrides.
 * @param {Partial<Record<CaseDomain, number>>} picks - This run's `{ domain: id }` picks.
 * @return {RunScenarioRow | undefined} The row, or undefined when it may be stale.
 */
export function runExpenseRow(
  rows: readonly RunScenarioRow[] | undefined,
  isPreviousData: boolean,
  picks: Partial<Record<CaseDomain, number>>
): RunScenarioRow | undefined {
  const row = rows?.find((candidate) => candidate.domain === 'expenses');
  if (row === undefined || !isPreviousData) {
    return row;
  }
  // With no expenses pick the run uses the case's scenario; picking the case's
  // own scenario is the same thing.
  return (picks.expenses ?? row.caseScenarioId) === row.scenarioId
    ? row
    : undefined;
}

/** How long a covered (or missing) scenario's answer is trusted across window focuses. */
export const COVERED_RECHECK_AFTER_MS = 60_000;

/** The shortest gap between two focus re-reads of a gapped scenario. */
export const GAPPED_RECHECK_AFTER_MS = 10_000;

/**
 * Whether a window focus should re-read the expense list.
 *
 *   - Gapped: once the answer is ten seconds old. The callout links the scenario
 *     out for fixing, and the warning should clear soon after the user comes back,
 *     without a 500-row re-read on every focus.
 *   - Covered: once the answer is a minute old. A gap added in another tab would
 *     otherwise stay hidden for the 30-minute cache — the false "no gap" this
 *     callout exists to prevent — and a minute bounds the 500-row re-reads.
 *   - Missing from the list: once the list is a minute old, like covered. A
 *     scenario created in another tab after the list was read is found by a
 *     re-read (the list is newest first); one older than the 500 most recent, or
 *     deleted, never comes back, and the minute bounds those re-reads.
 *   - No scenario on the run, or the list never loaded: never. A failed list has
 *     its own Retry.
 *
 * @param {DomainScenarioOption[] | undefined} options - The loaded expense list.
 * @param {number | null} scenarioId - The run's expense scenario.
 * @param {number} dataUpdatedAt - When the list was last read (epoch ms).
 * @param {number} now - The current time (epoch ms).
 * @return {boolean} True when a focus should re-read.
 */
export function needsRecheck(
  options: DomainScenarioOption[] | undefined,
  scenarioId: number | null,
  dataUpdatedAt: number,
  now: number = Date.now()
): boolean {
  if (scenarioId === null || options === undefined) {
    return false;
  }
  const option = asExpenseScenarioOptions(options).find(
    (candidate) => candidate.id === scenarioId
  );
  const age = now - dataUpdatedAt;
  if (option !== undefined && option.uncoveredAreas.length > 0) {
    return age >= GAPPED_RECHECK_AFTER_MS;
  }
  return age >= COVERED_RECHECK_AFTER_MS;
}

/**
 * Copy. Composes the pre-run callout's title +
 * subtitle with the SAME `countText` / `pluralize` helpers the DPS coverage
 * banner and the 06-VER staleness banner use.
 *
 * Pure functions only — no React, no fetching, no rule. `ExpenseScenario::CoverageGaps`
 * decides WHICH areas have a gap; this file only says it out loud. That split is
 * deliberate: the Ruby class is already a mirror of the calculator's
 * `apply_loe_opex_overrides`, and a third copy of the rule (in TypeScript, where
 * no test can see the calculator) would go stale silently.
 */

/**
 * The consequence the gap callout exists to state: a matched area's silence is a
 * $0, not an inherited Scenario cost. "Resolves to", not "matches": each property
 * resolves to ONE winning area, so an area shadowed by a higher-priority one may
 * never charge anything — which is also why the title says "may".
 */
export const UNCOVERED_CONSEQUENCE_SENTENCE =
  'A property that resolves to one of them is charged $0 for that cost; it does not fall back to the default basin-tier cost.';

/**
 * The callout title — how many of the run's expense scenario's areas the
 * calculator charges $0 for at least one cost type. Not "do not author": an area
 * with a blank-amount, basis-less or one-time row of that type has authored it,
 * and is still charged $0.
 *
 * "May", because the count is an upper bound: it is scenario-intrinsic (no slot
 * resolution), so an uncovered area fully shadowed by a covered area in the same
 * scenario is counted even though it wins no property.
 *
 * @param {number} totalUncoveredAreas - How many uncovered areas the scenario has.
 * @return {string} The title sentence.
 */
export function uncoveredAreasTitle(totalUncoveredAreas: number): string {
  const areaWord = pluralize(
    totalUncoveredAreas,
    'expense area',
    'expense areas'
  );
  return `${countText(
    totalUncoveredAreas
  )} ${areaWord} may charge $0 for LOE or opex.`;
}

/**
 * The gapped scenario's clause — its name and how much of it is affected, e.g.
 * "Delaware Expenses (2 of 5 areas)". The base is the scenario's OWN area count,
 * not the case's areas: the gap is scenario-intrinsic (no slot resolution, no
 * package), so "2 of 5" reads the same wherever the scenario is used.
 *
 * @param {string} name - The scenario's name, as the run's row gives it.
 * @param {ExpenseScenarioOption} scenario - Its list row, with uncovered areas.
 * @return {string} The clause.
 */
export function scenarioGapClause(
  name: string,
  scenario: ExpenseScenarioOption
): string {
  const areaWord = pluralize(scenario.areaCount, 'area', 'areas');
  return `${name} (${countText(scenario.uncoveredAreas.length)} of ${countText(
    scenario.areaCount
  )} ${areaWord})`;
}

/**
 * The callout subtitle: the gapped scenario's clause, then the consequence
 * sentence.
 *
 * @param {string} name - The scenario's name, as the run's row gives it.
 * @param {ExpenseScenarioOption} scenario - Its list row, with uncovered areas.
 * @return {string} The subtitle.
 */
export function uncoveredAreasSubtitle(
  name: string,
  scenario: ExpenseScenarioOption
): string {
  return `${scenarioGapClause(name, scenario)}. ${UNCOVERED_CONSEQUENCE_SENTENCE}`;
}

/** How each cost type reads in an area's line. */
export const COST_TYPE_LABELS: Record<CoverageCostType, string> = {
  loe: 'LOE',
  oil_opex: 'oil opex',
  gas_opex: 'gas opex',
  water_opex: 'water opex',
};

/** How many uncovered areas the callout names before "and N more". */
export const NAMED_AREA_LIMIT = 5;

/**
 * One uncovered area's line: its name and the cost types charged $0, e.g.
 * "Delaware Tier 3: $0 for LOE, oil opex, gas opex, and water opex". Not
 * "missing": an area can have a row of that type and still be charged $0 (blank
 * amount, no percent basis, one-time percent, water-basis percent with no water
 * rate), and "missing" beside a row the user can see reads as wrong.
 *
 * @param {UncoveredArea} area - An area from `uncoveredAreas`.
 * @return {string} The line.
 */
export function uncoveredAreaLine(area: UncoveredArea): string {
  return `${area.name}: $0 for ${joinClauses(
    area.costTypes.map((costType) => COST_TYPE_LABELS[costType])
  )}`;
}

/**
 * The line after the named areas, when there are more than NAMED_AREA_LIMIT.
 *
 * @param {number} areaCount - Every uncovered area.
 * @return {string | null} "and N more areas", or null when every area is named.
 */
export function moreAreasLine(areaCount: number): string | null {
  const rest = areaCount - NAMED_AREA_LIMIT;
  return rest > 0
    ? `and ${countText(rest)} more ${pluralize(rest, 'area', 'areas')}`
    : null;
}

/**
 * The consequence the UNCHECKED notice exists to state. Deliberately symmetric —
 * it refuses BOTH readings. Blank space already meant "checked, clean", which is
 * the bug this notice fixes; replacing it with something that sounds like a
 * reported gap would just invert the lie.
 */
export const UNCHECKED_CONSEQUENCE_SENTENCE =
  'Its coverage is unknown — not reported as a gap, and not confirmed as covered. Check it before you run.';

/**
 * The unchecked-notice title, for a run whose expense scenario the loaded list
 * could not answer for.
 *
 * @param {string} scenarioName - The run's expense scenario.
 * @return {string} The title sentence.
 */
export function uncheckedScenarioTitle(scenarioName: string): string {
  return `Expense coverage could not be checked for ${scenarioName}.`;
}

/**
 * The unchecked-notice subtitle: why, then the consequence sentence.
 *
 * @return {string} The subtitle.
 */
export function uncheckedScenarioSubtitle(): string {
  return `It could not be read from the loaded Expense Scenario list. ${UNCHECKED_CONSEQUENCE_SENTENCE}`;
}
